/**
 * AnimationPlayer clip/track data (Godot-inspired keyframe tracks).
 */

import { uid } from './types';

export interface AnimKey {
  t: number;
  v: number;
}

export type AnimProperty =
  | 'position.x'
  | 'position.y'
  | 'position.z'
  | 'rotation.x'
  | 'rotation.y'
  | 'rotation.z'
  | 'scale.x'
  | 'scale.y'
  | 'scale.z';

export interface AnimTrack {
  id: string;
  /** Relative path from AnimationPlayer: ".." = parent, "." = self, "Child/Name" */
  path: string;
  property: AnimProperty;
  keys: AnimKey[];
}

export interface AnimClipData {
  name: string;
  length: number;
  loop: boolean;
  tracks: AnimTrack[];
}

export type AnimLibrary = Record<string, AnimClipData>;

export const BUILTIN_CLIP_NAMES = ['spin_y', 'bob', 'pulse', 'none'] as const;

export function emptyClip(name: string, length = 1): AnimClipData {
  return { name, length, loop: true, tracks: [] };
}

export function defaultAnimLibrary(): AnimLibrary {
  return {
    spin_y: {
      name: 'spin_y',
      length: 2,
      loop: true,
      tracks: [
        {
          id: uid('trk'),
          path: '..',
          property: 'rotation.y',
          keys: [
            { t: 0, v: 0 },
            { t: 2, v: 360 },
          ],
        },
      ],
    },
    bob: {
      name: 'bob',
      length: 1,
      loop: true,
      tracks: [
        {
          id: uid('trk'),
          path: '..',
          property: 'position.y',
          keys: [
            { t: 0, v: 0 },
            { t: 0.5, v: 0.2 },
            { t: 1, v: 0 },
          ],
        },
      ],
    },
    pulse: {
      name: 'pulse',
      length: 0.8,
      loop: true,
      tracks: [
        {
          id: uid('trk'),
          path: '..',
          property: 'scale.x',
          keys: [
            { t: 0, v: 1 },
            { t: 0.4, v: 1.12 },
            { t: 0.8, v: 1 },
          ],
        },
        {
          id: uid('trk'),
          path: '..',
          property: 'scale.y',
          keys: [
            { t: 0, v: 1 },
            { t: 0.4, v: 1.12 },
            { t: 0.8, v: 1 },
          ],
        },
        {
          id: uid('trk'),
          path: '..',
          property: 'scale.z',
          keys: [
            { t: 0, v: 1 },
            { t: 0.4, v: 1.12 },
            { t: 0.8, v: 1 },
          ],
        },
      ],
    },
  };
}

export function sampleTrack(track: AnimTrack, time: number, length: number, loop: boolean): number | null {
  const keys = track.keys;
  if (!keys.length) return null;
  let t = time;
  if (loop && length > 0) t = ((t % length) + length) % length;
  else t = Math.min(Math.max(0, t), length);

  if (keys.length === 1) return keys[0].v;
  if (t <= keys[0].t) return keys[0].v;
  if (t >= keys[keys.length - 1].t) return keys[keys.length - 1].v;

  for (let i = 0; i < keys.length - 1; i++) {
    const a = keys[i];
    const b = keys[i + 1];
    if (t >= a.t && t <= b.t) {
      const span = b.t - a.t || 1e-6;
      const u = (t - a.t) / span;
      return a.v + (b.v - a.v) * u;
    }
  }
  return keys[keys.length - 1].v;
}

export function sortTrackKeys(track: AnimTrack): void {
  track.keys.sort((a, b) => a.t - b.t);
}

export function parseAnimLibrary(raw: unknown): AnimLibrary {
  if (!raw || typeof raw !== 'object') return defaultAnimLibrary();
  const lib = raw as AnimLibrary;
  // Ensure at least builtins if empty
  if (!Object.keys(lib).length) return defaultAnimLibrary();
  return lib;
}

export function ensureClip(lib: AnimLibrary, name: string): AnimClipData {
  if (!lib[name]) lib[name] = emptyClip(name);
  return lib[name];
}
